import { useCallback } from "react";
import { useFormContext } from "react-hook-form";

import {
  CaseTemplateFormValues,
  WorkflowRuleTaskWithId,
  WorkflowRuleActionWithId,
} from "src/cases/templates/caseTemplateFormUtils";
import { generateInternalId } from "src/cases/templates/internalIdUtils";

/**
 * Hook to manage internal ID-based tracking for tasks and actions
 */
export const useIdKeyMapping = () => {
  const { getValues } = useFormContext<CaseTemplateFormValues>();

  const createWorkflowTask = useCallback(
    (taskKey: string) => {
      const tasks = getValues("attributes.case_tasks");
      const task = tasks?.find((t) => t.key === taskKey);
      return {
        task_key: taskKey,
        _internalId: task?._internalId || generateInternalId(),
      };
    },
    [getValues],
  );

  const createWorkflowAction = useCallback(
    (actionKey: string) => {
      const actions = getValues("attributes.case_actions");
      const action = actions?.find((a) => a.key === actionKey);
      return {
        action_key: actionKey,
        _internalId: action?._internalId || generateInternalId(),
        automation: [],
        required_tasks_keys: [],
      };
    },
    [getValues],
  );

  const createRequiredTask = useCallback(
    (taskKey: string) => {
      const tasks = getValues("attributes.case_tasks");
      const task = tasks?.find((t) => t.key === taskKey);
      return {
        task_key: taskKey,
        _internalId: task?._internalId || generateInternalId(),
      };
    },
    [getValues],
  );

  const isSameTask = useCallback(
    (task1: WorkflowRuleTaskWithId, task2: WorkflowRuleTaskWithId) => {
      return task1._internalId === task2._internalId;
    },
    [],
  );

  const isSameAction = useCallback(
    (action1: WorkflowRuleActionWithId, action2: WorkflowRuleActionWithId) => {
      return action1._internalId === action2._internalId;
    },
    [],
  );

  return {
    createWorkflowTask,
    createWorkflowAction,
    createRequiredTask,
    isSameTask,
    isSameAction,
  };
};
